import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'next-i18next';
import { RefObject, useEffect, useRef } from 'react';

import { Backdrop } from '@boss/ui';

const TITLE_ID = 'legal-guarantee-modal-title';

type Props = {
  locale: string;
  onClose: () => void;
  returnFocusRef?: RefObject<HTMLButtonElement | null>;
};

const LegalGuaranteeModal = ({ locale, onClose, returnFocusRef }: Props) => {
  const { t } = useTranslation(['product', 'common']);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const imageLocale = locale === 'fr' ? 'fr' : 'nl';

  const handleClose = () => {
    returnFocusRef?.current?.focus();
    onClose();
  };

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
      } else if (event.key === 'Tab') {
        // The dialog exposes a single focusable control: keep focus trapped on it.
        event.preventDefault();
        closeButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  return (
    <Backdrop className="z-modalBackdrop" onClick={handleClose} visible>
      <div
        aria-labelledby={TITLE_ID}
        aria-modal="true"
        className="z-modal max-w-315 rounded-b-modal rounded-l-modal max-h-8/10 fixed left-5 right-5 top-1/2 m-auto flex -translate-y-1/2 flex-col bg-white p-6 shadow-lg md:p-10"
        role="dialog"
      >
        <h2 className="pr-8" id={TITLE_ID}>
          {t('productPanel.legalGuarantee.modalTitle', { ns: 'product' })}
        </h2>
        <div className="mt-4 min-h-0 overflow-y-auto overflow-x-hidden">
          {/* Official unaltered EU notice SVG — next/image optimization could affect the QR code, so plain <img> is intentional. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            alt={t('productPanel.legalGuarantee.alt', { ns: 'product' }) ?? ''}
            className="h-auto w-full"
            src={`/legalguarantee/legal-guarantee-${imageLocale}.svg`}
          />
        </div>
        <button
          aria-label={t('close', { ns: 'common' }) ?? ''}
          className="absolute right-6 top-6"
          onClick={handleClose}
          ref={closeButtonRef}
          type="button"
        >
          <FontAwesomeIcon className="text-xl" icon={faClose} />
        </button>
      </div>
    </Backdrop>
  );
};

export default LegalGuaranteeModal;
